import PropTypes from 'prop-types'
import React from 'react'
import { twMerge as mergeClassNames } from 'tailwind-merge'

import { formatDateTime } from '@/utils/formatters'

import DealerClosedCell from './components/DealerClosedCell'
import FilesCell from './components/FilesCell'
import RoNumberCell from './components/RoNumberCell'
import StatusBadge from './components/StatusBadge'
import { isIntakeOverdue, isOutstandingListDraft } from './overdue'

/**
 * Status groupings for the three RO Intake accordion sections.
 *
 * Values must match `REPAIR_ORDER_STATUS` on the backend
 * (warp_portal/apps/repair_orders/constants.py).
 */
export const INTAKE_STATUSES = ['Not Started', 'On Outstanding List']
export const ACTIVE_STATUSES = ['On Outstanding List', 'Closed', 'Closed — Submitted']
export const RESOLVED_STATUSES = ['Paid', 'Written Off', 'Paid with Adjustments']

/**
 * Status transitions offered by SetStatusModal for ROs that don't yet have an
 * Outstanding List record (i.e. in the Intake → OL path).
 *
 * Deliberately NOT derived from ACTIVE_STATUSES: that list controls the Active
 * accordion filter, which is a different concept. Adding a status to the filter
 * shouldn't silently widen what a worker can pick here.
 */
export const INTAKE_TRANSITIONS = [
  { value: 'On Outstanding List', label: 'On Outstanding List' },
  { value: 'Closed', label: 'Closed' },
  { value: 'Closed — Submitted', label: 'Closed — Submitted' },
]

/**
 * Status transitions offered by SetStatusModal when an RO is Closed and a WPC user is logging the
 * outcome.
 */
export const CLOSED_TRANSITIONS = [
  { value: 'Paid', label: 'Paid' },
  { value: 'Paid with Adjustments', label: 'Paid with Adjustments' },
]

StatusBadge.propTypes = {
  status: PropTypes.string,
}

/**
 * File types accepted by the RO attachment inputs (Add and Set Status modals).
 */
export const ACCEPTED_EXTENSIONS = [
  '.png',
  '.jpg',
  '.jpeg',
  '.pdf',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.ppt',
  '.pptx',
]

RoNumberCell.propTypes = {
  row: PropTypes.shape({
    state: PropTypes.string,
    dealer: PropTypes.string,
    id: PropTypes.string,
    roNumber: PropTypes.string,
  }).isRequired,
}

// Intake rows with an OL draft should show their draft state alongside the status.
const intakeDisplayStatus = (row) =>
  isOutstandingListDraft(row) ? 'On Outstanding List — Draft' : row.status

FilesCell.propTypes = {
  row: PropTypes.shape({
    fileCount: PropTypes.number,
    files: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        file: PropTypes.string,
        displayName: PropTypes.string,
      }),
    ),
  }).isRequired,
}

const RO_NUMBER_COLUMN = {
  name: 'RO #',
  selector: (row) => row.roNumber,
  cell: (row) => <RoNumberCell row={row} />,
  sortable: true,
  sortBy: 'ro_number',
  width: '225px',
}

const FILES_COLUMN = {
  name: 'Files',
  cell: (row) => <FilesCell row={row} />,
  width: '80px',
  center: true,
}

const DEALER_CLOSED_COLUMN = {
  name: 'Dealer Closed',
  selector: (row) => row.dealerClosed,
  cell: (row) => <DealerClosedCell row={row} />,
  sortable: true,
  sortBy: 'dealer_closed',
  width: '160px',
  center: true,
}

// Shared by every section so the overdue highlight is decided by the row's status
// (see isIntakeOverdue) rather than by which table it happens to be rendered in. A
// Closed claim sits in the Active section, so gating the highlight on the section
// was why it never turned red.
const DATE_RECEIVED_COLUMN = {
  name: 'Date Received',
  selector: (row) => row.receivedAt,
  cell: (row) => (
    <span className={mergeClassNames(isIntakeOverdue(row) && 'text-red font-semibold')}>
      {formatDateTime(row.receivedAt, 'MM/DD/YYYY hh:mm A')}
    </span>
  ),
  sortable: true,
  sortBy: 'received_at',
  width: '200px',
}

const LAST_UPDATED_COLUMN = {
  name: 'Last Updated',
  selector: (row) => row.modifiedAt,
  format: (row) => formatDateTime(row.modifiedAt, 'MM/DD/YYYY hh:mm A'),
  sortable: true,
  sortBy: 'modified_at',
  width: '200px',
}

const ADDED_BY_COLUMN = {
  name: 'Added By',
  selector: (row) => row.createdByName || '—',
  width: '200px',
}

export const INTAKE_COLUMNS = [
  RO_NUMBER_COLUMN,
  {
    name: 'Status',
    selector: (row) => row.status,
    cell: (row) => <StatusBadge status={intakeDisplayStatus(row)} />,
    sortable: true,
    sortBy: 'status',
    width: '300px',
  },
  FILES_COLUMN,
  DATE_RECEIVED_COLUMN,
  LAST_UPDATED_COLUMN,
  ADDED_BY_COLUMN,
]

export const ACTIVE_COLUMNS = [
  RO_NUMBER_COLUMN,
  {
    name: 'Status',
    selector: (row) => row.status,
    cell: (row) => <StatusBadge status={row.status} />,
    sortable: true,
    sortBy: 'status',
    width: '300px',
  },
  DEALER_CLOSED_COLUMN,
  FILES_COLUMN,
  DATE_RECEIVED_COLUMN,
  LAST_UPDATED_COLUMN,
  ADDED_BY_COLUMN,
]

export const RESOLVED_INTAKE_COLUMNS = [
  RO_NUMBER_COLUMN,
  {
    name: 'Status',
    selector: (row) => row.status,
    cell: (row) => <StatusBadge status={row.status} />,
    sortable: true,
    sortBy: 'status',
    width: '300px',
  },
  DEALER_CLOSED_COLUMN,
  FILES_COLUMN,
  DATE_RECEIVED_COLUMN,
  LAST_UPDATED_COLUMN,
  ADDED_BY_COLUMN,
]
